import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  Ban,
  Database,
  Eye,
  EyeOff,
  FileText,
  Globe,
  GlobeLock,
  History,
  Link2,
  Lock,
  MoreHorizontal,
  Pencil,
  Pin,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  Unlock,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BlockContent,
  BlockEditor,
  SaveIndicator,
  useAutosave,
  type BlockEditorHandle,
  type MentionItem,
} from '@/components/ui/block-editor';
import { PageBreadcrumbs, ancestorsOf, type Crumb } from '@/components/ui/PageBreadcrumbs';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useMembers } from '@/features/members/queries';
import { KbSyncBadge, useSourceStatus } from '@/features/knowledge_base';
import { pagesApi } from '../api';
import { makeWikiPageProvider } from '../pageProvider';
import {
  pagesHooks,
  stickiesHooks,
  usePageAction,
  usePageVersions,
  useRecordVisit,
  useSyncPageToRag,
} from '../queries';
import { rows } from '../rows';
import type { WikiPage, WikiPageVersion } from '../types';
import { SharePageDialog } from './SharePageDialog';

export function WikiPageEditor({
  page,
  allPages,
  onAddChild,
  onOpenPage,
}: {
  page: WikiPage;
  /** Sibling pages (for the breadcrumb ancestor chain); omitted in single-page views. */
  allPages?: WikiPage[];
  onAddChild: (parent: number) => void;
  /** Open another page by public_id — powers page-embed link navigation. */
  onOpenPage?: (publicId: string) => void;
}) {
  const update = pagesHooks.useUpdate();
  const remove = pagesHooks.useRemove();
  const visit = useRecordVisit();
  const sticky = stickiesHooks.useCreate();
  const archive = usePageAction((id: string) => pagesApi.archive(id));
  const restore = usePageAction((id: string) => pagesApi.restore(id));
  const publish = usePageAction((id: string) => pagesApi.publish(id));
  const unpublish = usePageAction((id: string) => pagesApi.unpublish(id));
  const syncToRag = useSyncPageToRag();
  const syncStatus = useSourceStatus('wiki.wikipage', [page.id]);
  const kbStatus = syncStatus.data?.get(page.id);
  const [title, setTitle] = useState(page.title);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canEdit = page.my_permission === 'edit' && !page.is_locked && !page.is_archived;
  const members: MentionItem[] = rows(useMembers({ type: 'staff', is_active: true }).data).map(
    (m) => ({ id: m.id, label: m.user.full_name || m.user.email }),
  );
  const pageProvider = useMemo(
    () => (onOpenPage ? makeWikiPageProvider(onOpenPage, page.public_id) : undefined),
    [onOpenPage, page.public_id],
  );
  const crumbs = useMemo<Crumb[]>(
    () => [
      { label: 'Wiki' },
      ...(allPages ? ancestorsOf(allPages, page) : []).map((a) => ({
        label: a.title || 'Untitled',
        icon: <FileText className="size-3.5 shrink-0" />,
        onClick: onOpenPage ? () => onOpenPage(a.public_id) : undefined,
      })),
      { label: page.title || 'Untitled', icon: <FileText className="size-3.5 shrink-0" /> },
    ],
    [allPages, page, onOpenPage],
  );

  // Record a recent-open whenever a different page is shown. The component is
  // keyed by page id upstream, so this runs once per page open.
  useEffect(() => {
    visit.mutate(page.public_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.public_id]);

  const patch = (input: Partial<WikiPage>) => update.mutate({ id: page.public_id, input });

  // View-first: pages open read-only; editors switch to write mode via "Edit".
  const [isEditing, setIsEditing] = useState(false);
  const persistBody = useCallback(
    (value: { json: JSONContent; html: string }, cbs: { onSuccess: () => void; onError: () => void }) =>
      update.mutate(
        {
          id: page.public_id,
          input: { body_json: value.json as unknown as Record<string, unknown>, body: value.html },
        },
        { onSuccess: cbs.onSuccess, onError: cbs.onError },
      ),
    [update, page.public_id],
  );
  const { state: saveState, onChange: onBodyChange, onSave: onBodySave } = useAutosave(persistBody);
  const editorRef = useRef<BlockEditorHandle>(null);
  const finishEditing = () => {
    editorRef.current?.flush();
    setIsEditing(false);
  };

  return (
    <div className="space-y-3">
      {/* Top bar */}
      <div className="flex items-center gap-2">
        <PageBreadcrumbs items={crumbs} />
        <div className="ml-auto flex items-center gap-1.5">
          {page.published_at ? (
            <Badge className="bg-emerald-600">Live</Badge>
          ) : (
            <Badge variant="secondary" title="Only people with access see this page until you publish it">
              Draft
            </Badge>
          )}
          {page.is_locked ? <Badge variant="outline">Locked</Badge> : null}
          <KbSyncBadge status={kbStatus} />
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Copy link to this page"
            title="Copy link"
            onClick={() => {
              const url = `${window.location.origin}/wiki?page=${page.public_id}`;
              void navigator.clipboard?.writeText(url);
              toast.success('Link copied');
            }}
          >
            <Link2 className="size-4" />
          </Button>
          {canEdit && isEditing ? (
            <>
              <SaveIndicator state={saveState} className="mr-1" />
              <Button
                variant="outline"
                size="sm"
                // Always clickable; flush() is a no-op on a clean doc, so a
                // redundant click never hits the server (same guard as autosave).
                onClick={() => editorRef.current?.flush()}
              >
                Save
              </Button>
              <Button size="sm" onClick={finishEditing}>
                Done
              </Button>
            </>
          ) : canEdit ? (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
          ) : null}
          {page.my_permission === 'edit' && !page.is_archived ? (
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
              <Share2 className="size-4" /> Share
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label="Page actions">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => sticky.mutate({ page: page.id })}>
                <Pin className="size-4" /> Pin to Home
              </DropdownMenuItem>
              {page.my_permission === 'edit' && !page.is_archived ? (
                <>
                  <DropdownMenuItem onClick={() => onAddChild(page.id)}>
                    <Plus className="size-4" /> Add sub-page
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => patch({ visibility: page.visibility === 'public' ? 'private' : 'public' })}
                  >
                    {page.visibility === 'public' ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    Make {page.visibility === 'public' ? 'private' : 'public'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => patch({ is_locked: !page.is_locked })}>
                    {page.is_locked ? <Unlock className="size-4" /> : <Lock className="size-4" />}
                    {page.is_locked ? 'Unlock' : 'Lock'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      page.published_at ? unpublish.mutate(page.public_id) : publish.mutate(page.public_id)
                    }
                  >
                    {page.published_at ? <GlobeLock className="size-4" /> : <Globe className="size-4" />}{' '}
                    {page.published_at ? 'Unpublish' : 'Publish'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                    <History className="size-4" /> History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => syncToRag.mutate(page.id)}>
                    <Database className="size-4" />{' '}
                    {kbStatus?.synced ? 'Re-sync to Knowledge Base' : 'Sync to Knowledge Base'}
                  </DropdownMenuItem>
                  {page.kb_excluded && !page.exclude_from_kb ? (
                    <DropdownMenuItem disabled>
                      <Ban className="size-4" /> Excluded from KB (via a parent)
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => patch({ exclude_from_kb: !page.exclude_from_kb })}
                    >
                      <Ban className="size-4" />{' '}
                      {page.exclude_from_kb
                        ? 'Include in Knowledge Base'
                        : 'Exclude from Knowledge Base'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => archive.mutate(page.public_id)}>
                    <Archive className="size-4" /> Archive
                  </DropdownMenuItem>
                </>
              ) : page.is_archived && page.my_permission === 'edit' ? (
                <>
                  <DropdownMenuItem onClick={() => restore.mutate(page.public_id)}>
                    <RotateCcw className="size-4" /> Restore
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => setConfirmDelete(true)}>
                    <Trash2 className="size-4" /> Delete
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                  <History className="size-4" /> History
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Title + icon on one line */}
      <div className="flex max-w-5xl items-center gap-2">
        <EmojiPicker
          compact
          value={page.icon}
          onChange={(icon) => patch({ icon })}
          placeholder="Page icon"
          disabled={!canEdit || !isEditing}
        />
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== page.title && patch({ title })}
          placeholder="Untitled"
          disabled={!canEdit || !isEditing}
          // `md:text-5xl` overrides Input's base `md:text-sm`; `disabled:` resets
          // keep the title crisp (not faded/greyed) in read-only view mode.
          className="h-auto min-w-0 flex-1 border-0 px-0 text-xl font-bold leading-tight tracking-tight shadow-none focus-visible:ring-0 disabled:cursor-text disabled:opacity-100 md:text-2xl"
        />
      </div>

      {/* Body — capped to a readable content column, not full-page width. */}
      <div className="max-w-5xl">
        {canEdit && isEditing ? (
          <BlockEditor
            ref={editorRef}
            value={page.body_json as unknown as JSONContent}
            html={page.body}
            members={members}
            pageProvider={pageProvider}
            placeholder="Type “/” for commands…"
            onChange={onBodyChange}
            onSave={onBodySave}
          />
        ) : (
          <div className="pt-[15px] pl-1 text-sm">
            <BlockContent
              value={page.body_json as unknown as JSONContent}
              html={page.body}
              pageProvider={pageProvider}
            />
          </div>
        )}
      </div>

      <HistoryDialog page={page} open={historyOpen} onOpenChange={setHistoryOpen} canEdit={canEdit} />
      <SharePageDialog page={page} open={shareOpen} onOpenChange={setShareOpen} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{page.title || 'Untitled'}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the page. Its sub-pages are kept and moved up one level.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate(page.public_id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HistoryDialog({
  page,
  open,
  onOpenChange,
  canEdit,
}: {
  page: WikiPage;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  canEdit: boolean;
}) {
  const versionsQuery = usePageVersions(open ? page.public_id : null);
  const restoreVersion = usePageAction((vid: number) => pagesApi.restoreVersion(page.public_id, vid));
  const versions = (versionsQuery.data ?? []) as WikiPageVersion[];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Page history</DialogTitle>
        </DialogHeader>
        {versionsQuery.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : versions.length === 0 ? (
          <p className="text-muted-foreground text-sm">No previous versions yet.</p>
        ) : (
          <ul className="divide-y">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="text-muted-foreground">v{v.version_number}</span>
                <span className="flex-1 truncate">{v.title || 'Untitled'}</span>
                <span className="text-muted-foreground text-xs">
                  {new Date(v.created).toLocaleString()}
                </span>
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restoreVersion.isPending}
                    onClick={() => restoreVersion.mutate(v.id, { onSuccess: () => onOpenChange(false) })}
                  >
                    Restore
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
